import './App.css';

import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { HostApiLocal, HostOfficialServer, getJwtObj } from './SharedCommon/utils';
import { IBrowserInfo, detectBrowser } from './utils/browserDetector';
import React, { Suspense, lazy } from 'react';
import { Variant, getRuntimeVariant } from './SharedCommon/variant';

import { BlockedBrowserWarning } from './components/BrowserWarning';
import GTAppSocket from './dataProvider/appSocket';
import Header from './components/Header';
import { IAppSessionSettings } from './dataProvider/IAppSessionSettings';
import Loading from './components/common/Loading';
import Notify from './components/Notify';
import { PromptPickGroup } from './components/Dialogues';
import ProtectRoute from './components/common/ProtectRoute';
import { SearchContext } from './ISearchContextType';
import { ShowLoadingContext } from './ShowLoadingContext';
import { ShowSearchBarContext } from './ShowSearchBarContext';
import SignInHeaderWrapper from './SharedCommon/SignInHeaderWrapper';
import Spinner from './components/Spinner';
import Welcome from './components/Welcome';
import i18n from './i18n';
import { _dataProvider } from './dataProvider/DataProvider';
import { getOrgIdFromUrl } from './SharedCommon/OrgList';
import { openMeeting } from './SharedCommon/Meeting';

const DiscoverStudy = lazy(() => import('./components/DiscoverStudy'));
const WritingBooks = lazy(() => import('./components/developClass/WritingBooks'));
const MgmtOrgGroupList = lazy(() => import('./components/MgmtOrgGroupList'));
const GroupMgmt = lazy(() => import('./components/GroupMgmt'));
const AppDownload = lazy(() => import('./components/AppDownload'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const Share2 = lazy(() => import('./components/Share2'));
const Join = lazy(() => import('./components/Join'));
const PubLesson = lazy(() => import('./components/PubLesson'));
const ClassRegistration = lazy(() => import('./components/ClassRegistration'));
const ClassManagement = lazy(() => import('./components/ClassManagement'));
const DeleteUser = lazy(() => import('./components/DeleteUser'));
const MySettings = lazy(() => import('./components/MySettings'));
const ProgramCreate = lazy(() => import('./components/developChannel/ProgramCreate'));
const ProgramDetail = lazy(() => import('./components/developChannel/ProgramDetail'));
const ChannelManage = lazy(() => import('./components/developChannel/ChannelManage'));
const GroupBreakoutRoom = lazy(() => import('./components/GroupBreakoutRoom'));

const forPwa = window.location.hostname.startsWith('pwa') || window.location.pathname.startsWith('/pwa');

interface IAppState {
  headButtonIdx: number;
  signInState: boolean;
  HeadClickCount: number;
  useEditor: number;
  pickGroup4Online: boolean;
  file: any;
  msg: string;
  deferredPrompt: any;
  installedEvent: any;
  searchQuery: any;
  showSearchBar: boolean;
  loadingText: string;
  showLoading: boolean;
  placeholder: string; // search bar placeholder for GroupMgmt.tsx
  messageCount: number;
  messageData: any;
  browserInfo: IBrowserInfo;
}

class App extends React.Component<object, IAppState> {
  private serverUrl: string;
  private loginUrl: string;
  private settings: IAppSessionSettings;
  private _meetingWindow: any;
  private _meetingGroupId: number;
  private _orgId: number;
  private _handleLanguageChange: (lng: string) => void;
  private gtAppSocket: GTAppSocket;

  constructor(props: Record<string, never>) {
    super(props);

    this.gtAppSocket = new GTAppSocket();
    this._orgId = getOrgIdFromUrl();
    this._meetingWindow = null;
    this._meetingGroupId = -1;

    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('localapi') === '1') {
      this.serverUrl = HostApiLocal;
    } else {
      this.serverUrl = HostOfficialServer;
    }
    this.loginUrl = this.serverUrl + '/user/login';

    let lastSettings = localStorage.getItem('iDigestSettings');
    console.log('lastSettings from storage = ', lastSettings, typeof lastSettings);
    if (!lastSettings || lastSettings.charAt(0) !== '{') {
      lastSettings = '{"headButtonIdx":1, "sessionIdx":0, "name":"", "chapterIdx":-1}';
    }
    this.settings = JSON.parse(lastSettings);
    if (this.settings.lang) {
      i18n.changeLanguage(this.settings.lang);
    }
    this._handleLanguageChange = (lng: string) => {
      this._saveSettings({ lang: lng } as IAppSessionSettings);
    };
    i18n.on('languageChanged', this._handleLanguageChange);

    const jwtObj = getJwtObj();
    console.log('signInState at start: ', jwtObj);

    this.state = {
      headButtonIdx: 1,
      HeadClickCount: 0,
      signInState: jwtObj.userId !== -1,
      useEditor: parseInt(urlParams.get('editor') || ''),
      pickGroup4Online: false,
      file: null,
      msg: '',
      deferredPrompt: null,
      installedEvent: null,
      searchQuery: '',
      showSearchBar: true,
      showLoading: false,
      loadingText: '',
      placeholder: '',
      messageCount: 0,
      messageData: undefined,
      browserInfo: detectBrowser()
    };

    _dataProvider.init(this.serverUrl, this._orgId);

    if (forPwa) {
      if (urlParams.has('tk')) {
        localStorage.setItem('access_token', urlParams.get('tk') || '');

        const urlObj = new URL(window.location.href);
        urlParams.delete('tk');
        urlObj.search = urlParams.toString();
        const newUrl = urlObj.toString();
        window.history.replaceState({}, '', newUrl);
        return;
      }
      if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          console.log('message listener event:', event.data);
          this.setState({ file: event.data.file, msg: event.data.msg });
        });
      }
      window.addEventListener('beforeinstallprompt', async (e) => {
        e.preventDefault();
        const deferredPrompt = e;
        this.setState({ deferredPrompt });
        console.log("'beforeinstallprompt' event was fired.");
      });

      window.addEventListener('appinstalled', (e) => {
        const deferredPrompt = null;
        const installedEvent = e;
        this.setState({ deferredPrompt, installedEvent });
        console.log('PWA was installed');
      });
    }

    console.log('the state.signInState, useEditor', this.state.signInState, this.state.useEditor);
  }

  updateSearchQuery = (newQuery: string) => {
    this.setState({ searchQuery: newQuery });
  };

  setSearchBarVisibility = (b: boolean) => {
    this.setState({ showSearchBar: b, searchQuery: '' });
  };
  setLoadingVisibility = (showLoading: boolean, loadingText?: string) => {
    this.setState({ showLoading, loadingText: loadingText || '' });
  };
  setPlaceholder = (s: string) => {
    this.setState({ placeholder: s, searchQuery: '' });
  };

  private onNewMessage = (data: any) => {
    console.log('App socket onNewMessage: ', data);
    if (!window.location.href.includes('/#/group')) {
      this.setState({ messageCount: this.state.messageCount + 1, messageData: undefined });
    } else {
      this.setState({ messageCount: 0, messageData: data });
    }
  };

  public componentDidMount() {
    console.log('Mounted');
    if (forPwa) {
      document.body.style.minWidth = '0';
    }
    this.gtAppSocket.init({ onNewMessage: this.onNewMessage });
  }

  public componentWillUnmount() {
    i18n.off('languageChanged', this._handleLanguageChange);
  }

  private _signInOut = (signInState: boolean): void => {
    console.log('Sign-in state changed in App:', signInState);

    if (!signInState) {
      const lang = this.settings.lang;
      this.settings = { headButtonIdx: 1, sessionIdx: 0, name: '', chapterIdx: -1, lang };
      localStorage.setItem('iDigestSettings', JSON.stringify(this.settings));
      _dataProvider.reset();
      this.setState({ messageCount: 0 });
    } else {
      this.gtAppSocket.init({ onNewMessage: this.onNewMessage });
    }

    this.setState({ signInState });
  };

  public render(): React.ReactNode {
    console.log('Rendering App, signInState:', this.state.signInState);

    // 浏览器检测：如果是被禁止的浏览器，显示警告页面
    if (this.state.browserInfo.isBlocked) {
      return (
        <BlockedBrowserWarning browserName={this.state.browserInfo.name} isMobile={this.state.browserInfo.isMobile} />
      );
    }

    if (window.location.href.indexOf('programDetail') !== -1) {
      return (
        <HashRouter>
          <Routes>
            <Route path='/programDetail' element={<ProgramDetail />}></Route>
          </Routes>
        </HashRouter>
      );
    }
    if (window.location.href.indexOf('lesson') !== -1) {
      return (
        <HashRouter>
          <Suspense fallback={<Spinner label='Loading...' />}>
            <Routes>
              <Route path='/lesson/:id/:file' element={<PubLesson />}></Route>
            </Routes>
          </Suspense>
        </HashRouter>
      );
    }

    if (forPwa) {
      return this._installPwa();
    }
    return (
      <SearchContext.Provider
        value={{
          searchQuery: this.state.searchQuery,
          updateSearchQuery: this.updateSearchQuery
        }}>
        <ShowLoadingContext.Provider
          value={{
            showLoading: this.state.showLoading,
            setShowLoading: this.setLoadingVisibility
          }}>
          <ShowSearchBarContext.Provider
            value={{
              showSearchBar: this.state.showSearchBar,
              setVisible: this.setSearchBarVisibility
            }}>
            <Notify count={this.state.messageCount} />
            <HashRouter>
              <div id='idHeaderMenuBar' className='headerDiv'>
                <Header
                  buttonIdx={this.state.headButtonIdx}
                  signInOut={this._signInOut}
                  setClickCount={this._setClickCount}
                  loginUrl={this.loginUrl}
                  signInState={this.state.signInState}
                  preview={window.location.href.indexOf('preview') !== -1}
                  getOnline={this._getOnline}
                />
              </div>
              {this.state.showLoading && (
                <div className='loading-page'>
                  <Loading label={this.state.loadingText} />
                </div>
              )}
              <Suspense fallback={<Spinner label='Loading...' />}>
                <Routes>
                  {getRuntimeVariant() === Variant.fishmeet && (
                    <Route path='/' element={<Navigate to='/breakout-room-mgnt' replace />} />
                  )}
                  <Route
                    path={getRuntimeVariant() === Variant.idigest ? '/' : '/discover-study'}
                    element={
                      <DiscoverStudy
                        signInState={this.state.signInState}
                        HeadClickCount={this.state.HeadClickCount}
                        saveSettings={this._saveSettings}
                        settings={this.settings}
                      />
                    }></Route>
                  <Route path='/share' element={<Share2 file={this.state.file} msg={this.state.msg} />}></Route>
                  <Route path='/join' element={<Join signInState={this.state.signInState} />}></Route>
                  <Route path='/AboutUs' element={<AboutUs />}></Route>
                  <Route
                    path='/group'
                    element={
                      <GroupMgmt
                        signInState={this.state.signInState}
                        HeadClickCount={this.state.HeadClickCount}
                        pickgroup={this._pickGroup}
                        messageData={this.state.messageData}
                      />
                    }></Route>
                  <Route
                    path='/org'
                    element={
                      <MgmtOrgGroupList
                        HeadClickCount={this.state.HeadClickCount}
                        signInState={this.state.signInState}
                        saveSettings={this._saveSettings}
                        settings={this.settings}
                        headerHeight={52}
                      />
                    }></Route>
                  <Route
                    path='/develop'
                    element={
                      <WritingBooks
                        HeadClickCount={this.state.HeadClickCount}
                        signInState={this.state.signInState}
                        saveSettings={this._saveSettings}
                        settings={this.settings}
                        useEditor={this.state.useEditor}
                        preview={window.location.href.indexOf('preview') !== -1}
                      />
                    }></Route>
                  <Route path='/channelManage' element={<ChannelManage />} />
                  <Route path='/programCreate' element={<ProgramCreate />} />
                  <Route path='/programDetail' element={<ProgramDetail />} />
                  <Route
                    path='/appDownload'
                    element={
                      <AppDownload
                        signInState={this.state.signInState}
                        HeadClickCount={this.state.HeadClickCount}
                        saveSettings={this._saveSettings}
                        settings={this.settings}
                      />
                    }></Route>
                  <Route
                    path='/classregister/:trainingId/:joinToken'
                    element={
                      <ClassRegistration
                        signInState={this.state.signInState}
                        contentHeight='100vh'
                        onSignInOut={this._signInOut}
                      />
                    }
                  />
                  <Route path='/my-settings' element={<MySettings />} />
                  <Route path='/classmanagement' element={<ClassManagement />} />
                  <Route path='/userDeletion' element={<DeleteUser />} />
                  <Route
                    path='/breakout-room-mgnt'
                    element={
                      <ProtectRoute signInState={this.state.signInState}>
                        <GroupBreakoutRoom
                          onJoinMeeting={this._pickGroup}
                          signInState={this.state.signInState}
                          headClickCount={this.state.HeadClickCount}
                          messageData={this.state.messageData}
                        />
                      </ProtectRoute>
                    }></Route>
                  <Route path='/welcome' element={<Welcome signInOut={this._signInOut} />}></Route>
                </Routes>
              </Suspense>
              {this.state.pickGroup4Online && <PromptPickGroup pickGroup={this._pickGroup} />}
            </HashRouter>
          </ShowSearchBarContext.Provider>
        </ShowLoadingContext.Provider>
      </SearchContext.Provider>
    );
  }

  private _setClickCount = (headButtonIdx: number) => {
    console.log('_setClickCount: ', this.state.searchQuery);
    this.settings.headButtonIdx = headButtonIdx;
    const HeadClickCount = this.state.HeadClickCount + 1;
    this.setState({ HeadClickCount, headButtonIdx, searchQuery: '', messageCount: 0 });
  };

  private _saveSettings = (settings?: IAppSessionSettings) => {
    console.log('_saveSettings 1:', settings);
    if (settings) {
      this.settings = { ...this.settings, ...settings };
    }
    console.log('_saveSettings 2: ', this.settings);
    localStorage.setItem('iDigestSettings', JSON.stringify(this.settings));
  };

  private _getOnline = () => {
    this.setState({ pickGroup4Online: true });
  };

  private _pickGroup = async (id: number, extra?: { enablePreBreakout?: boolean }): Promise<void | boolean> => {
    const { enablePreBreakout } = extra ?? {};
    this.setState({ pickGroup4Online: false });
    if (!id) {
      return;
    }

    try {
      await openMeeting({
        groupId: id,
        enablePreBreakout
      });

      return true;
    } catch (err) {
      console.log('Error opening meeting window:', err);
    }
    return;
  };

  private _installPwa = () => {
    if (this.state.installedEvent) {
      return (
        <div style={{ margin: '30px', textAlign: 'center' }}>
          <p>安装完成, 你可以&quot;关闭&quot;这个页面, 开始使用向&quot;小鸽子&quot;分享的功能</p>
          <br></br>
          <br></br>
          <br></br>
          <button
            onClick={async () => {
              window.close();
            }}
            style={{ width: '8em', borderRadius: '25px' }}>
            关闭
          </button>
        </div>
      );
    }
    if (window.location.href.indexOf('/share') !== -1) {
      return (
        <div style={{ margin: '30px', textAlign: 'center' }}>
          <p>分享进行中...</p>
          {!this.state.signInState && (
            <SignInHeaderWrapper
              signInOut={this._signInOut}
              loginUrl={this.loginUrl}
              pwa={true}
              signInState={this.state.signInState}
            />
          )}
          {this.state.signInState && (
            <HashRouter>
              <Routes>
                <Route path='/share' element={<Share2 file={this.state.file} msg={this.state.msg} />}></Route>
              </Routes>
            </HashRouter>
          )}
        </div>
      );
    }
    return (
      <div style={{ margin: '30px', textAlign: 'center' }}>
        <p>下载完成, 请点击以下按钮, 然后按照提示完成安装</p>
        <br></br>
        <br></br>
        <br></br>
        <button
          onClick={async () => {
            if (!this.state.deferredPrompt) {
              return;
            }
            this.state.deferredPrompt.prompt();
            const { outcome } = await this.state.deferredPrompt.userChoice;
            console.log(`User response to the install prompt: ${outcome}`);
            const deferredPrompt = null;
            this.setState({ deferredPrompt });
          }}
          style={{ width: '8em', borderRadius: '25px' }}>
          安装PWA
        </button>
        <br />
        <br />
        <br />
        <br />
        <br />
      </div>
    );
  };
}

export default App;
